import React, { useState } from 'react';
import { Clock, Loader2, MessageCircle } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';
import {
  getTeacherQaChannels,
  extractCourseTeacherIds,
  resolveTeacherQaChannels,
  type TeacherQaChannel,
} from '../../config/teacherQaChannels';

interface TeacherQaNoticeProps {
  courseId: string;
  coursePayload?: unknown;
}

const TelegramIcon: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

function openChannel(url: string, popup: Window | null) {
  if (popup && !popup.closed) {
    popup.location.href = url;
    popup.focus();
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

const TeacherQaNotice: React.FC<TeacherQaNoticeProps> = ({ courseId, coursePayload }) => {
  const [channels, setChannels] = useState<TeacherQaChannel[]>(() =>
    getTeacherQaChannels(extractCourseTeacherIds(coursePayload))
  );
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChannel = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isOpening) return;

    setError(null);
    setIsOpening(true);
    const popup = window.open('about:blank', '_blank');

    try {
      const resolved = await resolveTeacherQaChannels(courseId, coursePayload);
      setChannels(resolved);

      if (resolved.length === 1) {
        openChannel(resolved[0].url, popup);
        return;
      }

      popup?.close();
      if (resolved.length === 0) {
        setError('تعذر فتح قناة الأسئلة حالياً');
      }
    } catch {
      popup?.close();
      setError('تعذر فتح قناة الأسئلة حالياً');
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <section
      className={`${cardTheme.shell} ${cardTheme.accentTop} mb-5 sm:mb-7`}
      aria-labelledby="teacher-qa-heading"
    >
      <div className="p-4 sm:p-5 lg:p-6">
        <div className={`mb-3 ${cardTheme.badgeGold}`}>
          <MessageCircle className="h-3.5 w-3.5" />
          قناة الأسئلة العلمية
        </div>

        <h2
          id="teacher-qa-heading"
          className={`mb-2 text-lg font-bold leading-snug sm:text-xl ${cardTheme.title}`}
        >
          طلابنا الكرام
        </h2>

        <p className="text-sm leading-relaxed text-green-900/80 sm:text-base">
          تم تخصيص رقم لتلقي الأسئلة العلمية فقط (الأسئلة الخاصة بالمنهج فقط)
        </p>

        <div className="mt-3 flex min-h-11 w-full items-center gap-2 rounded-xl bg-gold-50 px-3 py-2 text-green-900 ring-1 ring-gold-200">
          <Clock className="h-4 w-4 shrink-0 text-gold-600" />
          <span className="text-xs font-semibold leading-snug sm:text-sm">
            مواعيد العمل يوميا من ١٠ ص ل١٠ م
          </span>
        </div>

        <p className={`mt-4 text-sm sm:text-base ${cardTheme.subtitle}`}>
          اضغط على الرابط دا هيدخلك على طول
        </p>

        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {channels.length > 1 ? (
            channels.map((channel) => (
              <a
                key={channel.url}
                href={channel.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex min-h-12 w-full flex-none items-center justify-center gap-2 rounded-xl px-4 text-sm sm:w-auto sm:min-w-[14rem] sm:text-base ${cardTheme.btnPrimary}`}
              >
                <TelegramIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                {channel.label}
              </a>
            ))
          ) : (
            <button
              type="button"
              onClick={handleOpenChannel}
              disabled={isOpening}
              className={`inline-flex min-h-12 w-full flex-none items-center justify-center gap-2 rounded-xl px-4 text-sm sm:w-auto sm:min-w-[14rem] sm:text-base disabled:cursor-wait disabled:opacity-80 ${cardTheme.btnPrimary}`}
            >
              {isOpening ? (
                <Loader2 className="h-4 w-4 animate-spin sm:h-5 sm:w-5" />
              ) : (
                <TelegramIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
              {isOpening ? 'جاري فتح القناة...' : 'افتح قناة الأسئلة'}
            </button>
          )}
        </div>

        {error && (
          <p className="mt-2 text-sm font-semibold text-red-700" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
};

export default TeacherQaNotice;
